/**
 * BasePlugin - Base class for all BaseModal plugins
 * Provides common interface and utilities for plugin development
 *
 * @class BasePlugin
 * @version 1.0.0
 */

'use strict';

/**
 * @typedef {Object} BaseModalInstance
 * @property {Function} show - Show modal method
 * @property {Function} close - Close modal method
 */

export default class BasePlugin {
    /**
     * Create a plugin instance
     * @param {BasePluginConfig} config - Plugin configuration
     */
    constructor(config = {}) {
        this.config        = config;
        this.name          = this.constructor.name;
        this.baseModal     = null;
        this.isInitialized = false;
    }

    /**
     * Initialize plugin with BaseModal reference
     * @param {BaseModalInstance} baseModal - BaseModal instance
     * @public
     */
    init(baseModal) {
        if (this.isInitialized) {
            if (this.config.debugMode) {
                console.warn(`${this.name}: Plugin already initialized`);
            }
            return;
        }

        this.baseModal     = baseModal;
        this.isInitialized = true;

        if (this.config.debugMode) {
            console.log(`${this.name}: Plugin initialized`, this.config);
        }
    }

    /**
     * Show modal with provided options
     * @param {string} modalId - Modal ID
     * @param {Object} options - Modal options
     * @returns {Promise} Modal instance promise
     * @protected
     */
    showModal(modalId, options = {}) {
        if (!this.baseModal) {
            console.error(`${this.name}: Plugin not initialized. Call init() first.`);
            return Promise.reject(new Error('Plugin not initialized'));
        }

        return this.baseModal.show(modalId, options);
    }

    /**
     * Close modal by ID
     * @param {string} modalId - Modal ID
     * @returns {Promise} Close promise
     * @protected
     */
    closeModal(modalId) {
        if (!this.baseModal) {
            return Promise.reject(new Error('Plugin not initialized'));
        }

        return this.baseModal.close(modalId);
    }

    /**
     * Destroy plugin instance and cleanup
     * @public
     */
    destroy() {
        this.baseModal     = null;
        this.isInitialized = false;

        if (this.config.debugMode) {
            console.log(`${this.name}: Plugin destroyed`);
        }
    }
}
